// Keyword casing transform for the IntelliSense ranking pipeline.
// PRD: Slice "Keyword Casing Settings" — Q7. Three modes:
//   * 'upper' — force keywords to UPPER CASE on insert.
//   * 'lower' — force keywords to lower case on insert.
//   * 'match-user' (default) — case follows the user's typed fragment, falling
//      back to dominant-case detected in the buffer when no signal is typed.
//
// Pure module. No appStore import — the orchestrator threads the resolved
// hint through `rankCandidates` (via options) and through the orchestrator's
// post-process for legacy LSP paths.

export type KeywordCasingMode = 'match-user' | 'upper' | 'lower';

export type KeywordCasingHint = {
  readonly mode: KeywordCasingMode;
  readonly typedFragment?: string;
  readonly bufferText?: string;
};

const COMMON_SQL_KEYWORDS = new Set([
  'select', 'from', 'where', 'join', 'inner', 'left', 'right', 'outer',
  'on', 'and', 'or', 'not', 'in', 'is', 'null', 'as', 'order', 'by',
  'group', 'having', 'union', 'all', 'distinct', 'limit', 'offset',
  'insert', 'into', 'values', 'update', 'set', 'delete', 'create',
  'table', 'view', 'index', 'drop', 'alter', 'add', 'column', 'with',
  'case', 'when', 'then', 'else', 'end', 'exists', 'between', 'like',
  'top', 'over', 'partition', 'desc', 'asc',
]);

const ALPHA = /[a-zA-Z]/;

export type ResolvedKeywordCase = 'upper' | 'lower' | 'preserve';

export function applyKeywordCasing(text: string, hint: KeywordCasingHint): string {
  return applyResolvedKeywordCase(text, resolveKeywordCase(hint));
}

/** Apply a case already resolved with {@link resolveKeywordCase}. */
export function applyResolvedKeywordCase(text: string, desired: ResolvedKeywordCase): string {
  if (desired === 'preserve') return text;
  return desired === 'upper' ? text.toUpperCase() : text.toLowerCase();
}

/**
 * The case `hint` asks for. Depends only on the hint — in `match-user` mode
 * with nothing typed it regex-scans the whole buffer — so callers casing a
 * whole list resolve it once and apply it per item (1.5.1 F2: per-item
 * resolution scanned the buffer ~3× per keyword on every provider call).
 */
export function resolveKeywordCase(hint: KeywordCasingHint): ResolvedKeywordCase {
  if (hint.mode === 'upper') return 'upper';
  if (hint.mode === 'lower') return 'lower';
  return resolveMatchUserCase(hint);
}

function resolveMatchUserCase(hint: KeywordCasingHint): 'upper' | 'lower' | 'preserve' {
  const typedCase = casingOf(hint.typedFragment ?? '');
  if (typedCase === 'upper' || typedCase === 'lower') return typedCase;
  if (typedCase === 'mixed') return 'preserve';
  const dominant = detectDominantKeywordCase(hint.bufferText ?? '');
  if (dominant) return dominant;
  return 'upper';
}

function casingOf(s: string): 'upper' | 'lower' | 'mixed' | 'none' {
  let upper = 0;
  let lower = 0;
  for (const ch of s) {
    if (ch >= 'A' && ch <= 'Z') upper++;
    else if (ch >= 'a' && ch <= 'z') lower++;
  }
  if (upper === 0 && lower === 0) return 'none';
  if (upper > 0 && lower === 0) return 'upper';
  if (lower > 0 && upper === 0) return 'lower';
  return 'mixed';
}

function detectDominantKeywordCase(buffer: string): 'upper' | 'lower' | null {
  if (buffer.length === 0) return null;
  let upperHits = 0;
  let lowerHits = 0;
  const wordRe = /[A-Za-z][A-Za-z0-9_]*/g;
  let match: RegExpExecArray | null;
  while ((match = wordRe.exec(buffer)) !== null) {
    const word = match[0];
    if (!COMMON_SQL_KEYWORDS.has(word.toLowerCase())) continue;
    const c = casingOf(word);
    if (c === 'upper') upperHits++;
    else if (c === 'lower') lowerHits++;
  }
  if (upperHits === 0 && lowerHits === 0) return null;
  return upperHits >= lowerHits ? 'upper' : 'lower';
}

/**
 * Test seam: extract the alphabetic-only typed fragment from a Monaco word
 * (`getWordUntilPosition().word`). Non-alpha leading characters (digits,
 * underscores) are not signal for keyword case — only alpha chars count.
 */
export function extractTypedFragment(monacoWord: string): string {
  const m = ALPHA.exec(monacoWord);
  if (!m) return '';
  return monacoWord;
}
